// Admin Transactions — read for all, export only for super_admin.
import React, { useEffect, useState } from 'react';
import { adminAxios, useAdminAuth } from './AdminAuthContext';
import { Loader2, Download, Search } from 'lucide-react';

const fmtBRL = (n) => `R$ ${Number(n || 0).toFixed(2).replace('.', ',')}`;
const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
};

const StatusBadge = ({ status }) => {
  const map = {
    confirmado: ['bg-[#DCFCE7] text-[#006030]', 'Pago'],
    pago: ['bg-[#DCFCE7] text-[#006030]', 'Pago'],
    pendente: ['bg-[#FEF3C7] text-[#92400E]', 'Pendente'],
    rejeitado: ['bg-[#FEE2E2] text-[#7F1D1D]', 'Falhou'],
    cancelado: ['bg-[#F4F6F8] text-[#475569]', 'Cancelado'],
  };
  const [cls, label] = map[status] || ['bg-[#F4F6F8] text-[#475569]', status || 'n/d'];
  return <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold ${cls}`}>{label}</span>;
};

const PLAN_LABEL = {
  mensal: 'Mensal', anual: 'Anual', pro_mensal: 'Mensal',
  semestral: '6 meses', trimestral: 'Trimestral',
};

const AdminTransactions = () => {
  const { token, isSuper } = useAdminAuth();
  const [data, setData] = useState({ transactions: [], count: 0 });
  const [filters, setFilters] = useState({ period: '30d', status: 'all', plan: 'all', search: '' });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(filters).toString();
      const r = await adminAxios(token).get(`/api/admin/transactions?${params}`);
      setData(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.period, filters.status, filters.plan]);

  const exportCsv = () => {
    if (!isSuper) return;
    const rows = [
      ['Nome', 'E-mail', 'Plano', 'Valor', 'Data', 'Status'],
      ...data.transactions.map((t) => [
        t.nome || '',
        t.email || '',
        PLAN_LABEL[t.tipo_plano] || t.tipo_plano || '',
        Number(t.valor || 0).toFixed(2).replace('.', ','),
        fmtDate(t.criado_em),
        t.status || '',
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tdo-transacoes-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="admin-transactions-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-black text-[#0F1C2E]">Financeiro</h1>
          <p className="text-xs text-[#475569]">{data.count} transações no período</p>
        </div>
        {isSuper && (
          <button onClick={exportCsv}
                  className="inline-flex items-center gap-1.5 bg-[#006030] hover:bg-[#004020] text-white px-3 py-2 rounded text-xs font-bold"
                  data-testid="admin-export-csv">
            <Download className="w-3.5 h-3.5" /> Exportar CSV
          </button>
        )}
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg p-3 grid grid-cols-1 sm:grid-cols-4 gap-2">
        <select value={filters.period} onChange={(e) => setFilters({ ...filters, period: e.target.value })}
                className="px-2 py-2 border border-[#E2E8F0] rounded text-xs" data-testid="filter-period">
          <option value="7d">Últimos 7 dias</option>
          <option value="30d">Últimos 30 dias</option>
          <option value="90d">Últimos 90 dias</option>
        </select>
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="px-2 py-2 border border-[#E2E8F0] rounded text-xs" data-testid="filter-status">
          <option value="all">Todos status</option>
          <option value="confirmado">Pagos</option>
          <option value="pendente">Pendentes</option>
          <option value="rejeitado">Falhou</option>
        </select>
        <select value={filters.plan} onChange={(e) => setFilters({ ...filters, plan: e.target.value })}
                className="px-2 py-2 border border-[#E2E8F0] rounded text-xs" data-testid="filter-plan">
          <option value="all">Todos planos</option>
          <option value="mensal">Mensal</option>
          <option value="anual">Anual</option>
        </select>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <input value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                 onBlur={load}
                 onKeyDown={(e) => e.key === 'Enter' && load()}
                 placeholder="Nome ou e-mail"
                 className="w-full pl-7 pr-2 py-2 border border-[#E2E8F0] rounded text-xs"
                 data-testid="filter-search" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#1E3A5F]" /></div>
      ) : data.transactions.length === 0 ? (
        <p className="text-sm text-[#94A3B8] text-center py-12">Nenhuma transação encontrada para esse filtro.</p>
      ) : (
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F8FAFC] text-[10px] uppercase tracking-wider text-[#475569] border-b border-[#E2E8F0]">
                <th className="px-3 py-2 text-left">Nome</th>
                <th className="px-3 py-2 text-left">E-mail</th>
                <th className="px-3 py-2 text-left">Plano</th>
                <th className="px-3 py-2 text-right">Valor</th>
                <th className="px-3 py-2 text-left">Data</th>
                <th className="px-3 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.transactions.map((t, i) => (
                <tr key={i} className="border-b border-[#F4F6F8] hover:bg-[#F8FAFC]">
                  <td className="px-3 py-2 text-[#0F1C2E] text-xs font-medium truncate max-w-[200px]">{t.nome || '—'}</td>
                  <td className="px-3 py-2 text-[#475569] text-xs truncate max-w-[200px]">{t.email || '—'}</td>
                  <td className="px-3 py-2 text-[#475569] text-xs">{PLAN_LABEL[t.tipo_plano] || t.tipo_plano}</td>
                  <td className="px-3 py-2 text-[#0F1C2E] text-xs font-bold text-right">{fmtBRL(t.valor)}</td>
                  <td className="px-3 py-2 text-[#475569] text-xs whitespace-nowrap">{fmtDate(t.criado_em)}</td>
                  <td className="px-3 py-2"><StatusBadge status={t.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminTransactions;
